import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config';
import { prepareHeaders } from './utils';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export interface LatamContract {
    subscription_id: number;
    subscription_status: 'ACTIVE' | 'CANCELLATION_PENDING';
    asset_id: string;
    sku: string;
    asset_status: string;
    product_name: string;
    period_description: string;
}

export const latamContractsApi = createApi({
    reducerPath: 'latamContractsApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.latamMarketplace, prepareHeaders }),
    endpoints: build => ({
        checkActiveContracts: build.query<LatamContract[], string>({
            query: (assetId: string | undefined) => ({
                url: '/v1/payment-gateway/subscription/assets-no-canceled',
                method: 'POST',
                body: {
                    ids: [assetId],
                },
            }),
            transformResponse: (rawResult, _meta, _assetId) => {
                return rawResult as LatamContract[];
            },
        }),
        checkActiveContractsBulk: build.query<(LatamContract & { id: string })[], string[]>({
            queryFn: async (
                assetIds,
                _queryApi,
                _extraOptions,
                fetchWithBQ
            ): Promise<{ data: (LatamContract & { id: string })[] } | { error: FetchBaseQueryError }> => {
                if (!assetIds?.length) {
                    return { data: [] };
                }

                const response = await fetchWithBQ({
                    url: '/v1/payment-gateway/subscription/assets-no-canceled',
                    method: 'POST',
                    body: { ids: assetIds },
                });

                if (response.error) {
                    return { error: response.error };
                }

                const contracts = response.data as LatamContract[];
                const contractsForAllAssets = contracts.map(it => ({
                    ...it,
                    id: it.asset_id,
                }));

                return { data: contractsForAllAssets };
            },
        }),
    }),
});

export const { useCheckActiveContractsQuery, useCheckActiveContractsBulkQuery } = latamContractsApi;
